import { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { logger } from '@/lib/logger';

const API_URL = import.meta.env.VITE_API_URL || '';

// Warn in development if API_URL is not configured
if (!import.meta.env.VITE_API_URL && import.meta.env.MODE === 'development') {
  logger.warn('VITE_API_URL environment variable is not set', {
    env: 'development',
  });
}

import { cn } from '@/lib/utils';
import { Logo, LogoIcon } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useNotifications } from '@/hooks/useNotifications';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  LayoutDashboard,
  Users,
  BarChart3,
  Settings,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Moon,
  Sun,
  Bell,
  Shield,
  Briefcase,
  X,
  Telescope,
  MessageSquare,
} from 'lucide-react';
import { GlobalSearch } from '@/components/crm/GlobalSearch';
import { FlowProvider } from '@/context/FlowContext';
import { FlowWidget } from '@/components/flow/FlowWidget';

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  href: string;
  badge?: number;
}

const getNavItems = (role: string | null, isAdmin: boolean): NavItem[] => {
  const items: NavItem[] = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, href: '/crm/dashboard' },
    { id: 'contacts', label: 'Network', icon: Users, href: '/crm/contacts', badge: 0 },
    { id: 'deals', label: 'Deal Flow', icon: Briefcase, href: '/crm/deals' },
    { id: 'reminders', label: 'Tasks & Reminders', icon: CheckSquare, href: '/crm/reminders' },
  ];

  // Show Discover to investors AND admins
  if (role === 'investor' || isAdmin) {
    items.push({ id: 'discover', label: 'Discover', icon: Telescope, href: '/crm/discover' });
  }

  items.push(
    { id: 'analytics', label: 'Analytics', icon: BarChart3, href: '/crm/analytics' },
    { id: 'flow', label: 'Flow', icon: MessageSquare, href: '/crm/flow' },
    { id: 'settings', label: 'Settings', icon: Settings, href: '/crm/settings' }
  );

  return items;
};

// Simple event bus for selected contact
export let selectedContactId: string | null = null;
export const setSelectedContact = (id: string | null) => { selectedContactId = id; };

export function CRMLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin, logout, token } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const { notifications, deleteNotification, clearAllNotifications } = useNotifications();
  
  // Fetch user profile to check role
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await fetch(`${API_URL}/api/user/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setUserRole(data.role);
        }
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      }
    };
    if (token) fetchProfile();
  }, [token]);
  
  // Load sidebar state from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('crm-sidebar-collapsed');
    if (saved !== null) {
      setSidebarCollapsed(JSON.parse(saved));
    }
  }, []);

  // Persist sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('crm-sidebar-collapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const p = location.pathname;
  // Flow page: redundant; Settings: dense bottom-right controls.
  const hideFlowWidget = p === '/crm/flow' || p.startsWith('/crm/settings');
  const showFlowWidget = !hideFlowWidget;
  // Tasks & Reminders: panel only (inline orb in page header); no floating launcher / extra scroll padding.
  const isTasksRemindersRoute =
    p === '/crm/tasks' ||
    p.startsWith('/crm/tasks/') ||
    p === '/crm/reminders' ||
    p.startsWith('/crm/reminders/');
  // Deal Flow / Network: inline orb in page header (not bottom-right).
  const isDealsRoute = p === '/crm/deals' || p.startsWith('/crm/deals/');
  const isContactsRoute = p === '/crm/contacts' || p.startsWith('/crm/contacts/');
  const flowPanelOnly = isTasksRemindersRoute || isDealsRoute || isContactsRoute;

  const getUserInitials = () => {
    if (user?.name) {
      return user.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return user?.email?.charAt(0).toUpperCase() || 'U';
  };

  const getUserDisplayName = () => {
    return user?.name || user?.email?.split('@')[0] || 'User';
  };

  return (
    <FlowProvider>
    <TooltipProvider delayDuration={0}>
      <div className="h-screen overflow-hidden bg-background">
        {/* Sidebar */}
        <aside
          style={{
            width: sidebarCollapsed ? 80 : 260,
            transition: 'width 0.3s ease',
          }}
          className={cn(
            'bg-sidebar text-sidebar-foreground fixed left-0 top-0 z-50 h-screen border-r border-sidebar-border',
            'flex flex-col shadow-lg shadow-black/5'
          )}
        >
          {/* Logo Section - Clickable to go home */}
          <div className="flex h-16 items-center justify-between border-b border-border/70 bg-white/70 px-4 backdrop-blur-sm dark:bg-white/70">
            {!sidebarCollapsed ? (
              <NavLink to="/matches" className="flex items-center gap-3 transition-opacity hover:opacity-90">
                <Logo size="sm" textClassName="fill-sidebar-foreground" />
              </NavLink>
            ) : (
              <NavLink to="/matches" className="mx-auto transition-opacity hover:opacity-90">
                <LogoIcon size={36} />
              </NavLink>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className={cn(
                  'h-8 w-8 rounded-lg text-sidebar-foreground/80 hover:bg-black/5 hover:text-sidebar-foreground',
                sidebarCollapsed && 'mx-auto'
              )}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-5">
            <nav className={cn('space-y-1.5 px-3', sidebarCollapsed && 'px-2')}>
              {getNavItems(userRole, isAdmin).map((item) => {
                const isActive = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
                const navLink = (
                  <NavLink
                    to={item.href}
                    end={false}
                    className={cn(
                      'group relative flex items-center gap-3 overflow-hidden rounded-xl px-3 py-2.5 text-sm font-medium transition-all cursor-pointer',
                      sidebarCollapsed && 'mx-auto h-11 w-11 justify-center gap-0 px-0 py-0 rounded-2xl',
                      isActive
                        ? 'bg-white text-[#1C5CCC] shadow-lg shadow-black/10 ring-1 ring-black/5'
                        : 'text-sidebar-foreground/80 hover:bg-black/5 hover:text-sidebar-foreground'
                    )}
                    style={{
                      transition:
                        'transform 280ms cubic-bezier(0.22, 1, 0.36, 1), background-color 260ms ease, color 220ms ease, box-shadow 280ms cubic-bezier(0.22, 1, 0.36, 1)',
                      transform: isActive ? 'translateX(3px) scale(1.01)' : 'translateX(0) scale(1)',
                    }}
                    aria-label={sidebarCollapsed ? item.label : undefined}
                  >
                    {!sidebarCollapsed && (
                      <span
                        aria-hidden
                        className={cn(
                          'absolute left-1 top-1/2 h-6 w-1 -translate-y-1/2 rounded-full bg-current transition-all duration-300 ease-out',
                          isActive ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-50'
                        )}
                      />
                    )}
                    <item.icon
                      className={cn(
                        'h-5 w-5 flex-shrink-0 transition-transform duration-300 ease-out',
                        isActive && 'text-[#1C5CCC] scale-110'
                      )}
                    />
                    {!sidebarCollapsed && (
                      <>
                        <span className={cn('flex-1 transition-all duration-300', isActive && 'translate-x-0.5')}>
                          {item.label}
                        </span>
                        {item.badge !== undefined && item.badge > 0 && (
                          <Badge className={cn(
                            'h-5 min-w-5 border border-border/70 bg-muted px-1 text-xs text-sidebar-foreground hover:bg-muted transition-all duration-300',
                            isActive && 'scale-105'
                          )}>
                            {item.badge}
                          </Badge>
                        )}
                      </>
                    )}
                  </NavLink>
                );

                if (!sidebarCollapsed) {
                  return <div key={item.id}>{navLink}</div>;
                }

                return (
                  <Tooltip key={item.id} delayDuration={80}>
                    <TooltipTrigger asChild>{navLink}</TooltipTrigger>
                    <TooltipContent
                      side="right"
                      sideOffset={10}
                      className="flex items-center gap-2 rounded-lg border border-border bg-popover px-3 py-1.5 text-sm font-medium text-popover-foreground shadow-lg"
                    >
                      {item.label}
                      {item.badge !== undefined && item.badge > 0 && (
                        <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-[#2B7FFF] px-1 text-[10px] font-medium text-white">
                          {item.badge}
                        </span>
                      )}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </nav>
          </ScrollArea>

          {/* Admin Badge */}
          {isAdmin && !sidebarCollapsed && (
            <div className="px-4 py-2">
              <div className="flex items-center gap-2 rounded-lg border border-border/70 bg-white/70 px-3 py-2 backdrop-blur-sm">
                <Shield className="h-4 w-4 text-sidebar-foreground" />
                <span className="text-xs font-medium text-sidebar-foreground">
                  Admin Access
                </span>
              </div>
            </div>
          )}

          {/* User Section */}
          {!sidebarCollapsed && user && (
            <div className="border-t border-border/70 bg-white/70 p-4">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 ring-2 ring-white/20">
                  <AvatarFallback className={cn(
                    'text-sm font-semibold',
                    isAdmin ? 'bg-white text-[#4D2B99]' : 'bg-white text-[#1C5CCC]'
                  )}>
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-medium text-sidebar-foreground">
                    {getUserDisplayName()}
                  </p>
                  <p className="text-xs capitalize text-sidebar-foreground/70">
                    {isAdmin ? 'Administrator' : 'Member'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </aside>

        {/* Header */}
        <header
          style={{
            left: sidebarCollapsed ? 80 : 260,
            transition: 'left 0.3s ease',
          }}
          className="fixed top-0 right-0 z-40 h-16 border-b border-border/70 bg-white/85 backdrop-blur-2xl dark:border-white/10 dark:bg-[#0C162B]/80 animate-slide-in-top"
        >
          <div className="flex h-full items-center justify-between px-4 lg:px-6">
            <div className="flex-1 max-w-xl">
              <GlobalSearch />
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleDarkMode}
                className="h-10 w-10 rounded-lg text-muted-foreground hover:bg-[#2B7FFF]/10 hover:text-[#2B7FFF]"
              >
                {isDarkMode ? (
                  <Moon className="h-5 w-5" />
                ) : (
                  <Sun className="h-5 w-5" />
                )}
              </Button>

              {/* Notifications */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative h-10 w-10 rounded-lg text-muted-foreground hover:bg-[#2B7FFF]/10 hover:text-[#2B7FFF]"
                  >
                    <Bell className="h-5 w-5" />
                    {notifications.length > 0 && (
                      <span className="absolute top-2 right-2 flex h-4 w-4 items-center justify-center rounded-full bg-[#2B7FFF] text-[10px] font-medium text-white">
                        {notifications.length}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80 border-border/70 shadow-xl">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-sm">Notifications</h4>
                      {notifications.length > 0 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 text-xs text-muted-foreground"
                          onClick={clearAllNotifications}
                        >
                          Clear all
                        </Button>
                      )}
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">No notifications</p>
                      ) : (
                        notifications.map((notification: { id: string; title: string; message?: string; createdAt?: string }) => (
                          <div key={notification.id} className="p-3 bg-muted rounded-lg text-sm group relative">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => deleteNotification(notification.id)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                            <p className="font-medium pr-6">{notification.title}</p>
                            <p className="text-xs text-muted-foreground mt-1">{notification.message}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="relative h-10 gap-2 rounded-lg px-2 hover:bg-[#2B7FFF]/10"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className={cn(
                        'text-white text-sm',
                        isAdmin ? 'bg-purple-500' : 'bg-[#2B7FFF]'
                      )}>
                        {getUserInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium leading-tight">{getUserDisplayName()}</p>
                      <p className="text-xs text-muted-foreground capitalize">
                        {isAdmin ? 'Admin' : 'Member'}
                      </p>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{getUserDisplayName()}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/account')}>
                    <Settings className="mr-2 h-4 w-4" />
                    Account Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 dark:text-red-400">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main
          style={{
            marginLeft: sidebarCollapsed ? 80 : 260,
            transition: 'margin-left 0.3s ease',
          }}
          className="mt-16 h-[calc(100vh-4rem)] overflow-hidden bg-[radial-gradient(860px_420px_at_8%_-8%,rgba(43,127,255,0.12),transparent_58%),radial-gradient(760px_360px_at_92%_-14%,rgba(14,165,233,0.08),transparent_60%),linear-gradient(180deg,#F8FBFF_0%,#F6FAFF_46%,#F2F7FF_100%)] dark:bg-[radial-gradient(1100px_540px_at_16%_-14%,rgba(59,130,246,0.22),transparent_55%),radial-gradient(900px_420px_at_86%_-2%,rgba(56,189,248,0.12),transparent_60%),linear-gradient(180deg,#0A1224_0%,#0D1830_44%,#111E38_100%)] animate-content-reveal"
        >
          <div
            className={cn(
              'h-full overflow-y-auto p-4 lg:p-6',
              showFlowWidget &&
                !flowPanelOnly &&
                'pb-[calc(13rem+env(safe-area-inset-bottom,0px))] pr-24 sm:pr-32'
            )}
          >
            <Outlet />
          </div>
        </main>

        {/* Flow: floating launcher except Tasks/Reminders (inline orb in Tasks header) */}
        {showFlowWidget && <FlowWidget panelOnly={flowPanelOnly} />}
      </div>
    </TooltipProvider>
    </FlowProvider>
  );
}

export default CRMLayout;

